/**
 * CredentialsTab Component
 *
 * Form for managing guide credentials:
 * - Guide License (number, state)
 * - Insurance (provider, policy number, expiry date)
 * - Certifications (stored in JSONB)
 *
 * Epic: MVP 1.21.3 - Guide Dashboard
 * Story: US-1.21.3-14: Credential Management (5 SP) - Partial implementation in tab
 *
 * Props:
 * - profile: object with current profile data
 * - onChange: function(field, value) - Called when field changes
 * - onSave: function() - Called when save button clicked
 * - isSaving: boolean
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Save as _Save, AlertCircle as _AlertCircle, Plus, X, Edit2 as _Edit2, Check, Save, AlertCircle } from 'lucide-react';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const CERTIFICATION_TYPES = [
  'CPR Certification',
  'First Aid',
  'Wilderness First Responder (WFR)',
  'Wilderness First Aid (WFA)',
  'Wilderness EMT',
  'Swift Water Rescue',
  'Avalanche Safety',
  'Rock Climbing Instructor',
  'Kayaking Instructor',
  'Fishing Guide License',
  'Hunting Guide License',
  'Leave No Trace Trainer',
  'NOLS Certification',
  'AMGA Certification',
  'Other',
];

export default function CredentialsTab({
  profile,
  onChange,
  onSave,
  isSaving = false
}) {
  const [showAddCert, setShowAddCert] = useState(false);
  const [_editingCertId, _setEditingCertId] = useState(null);
  const [newCert, setNewCert] = useState({
    name: '',
    issuer: '',
    issue_date: '',
    expiry_date: '',
  });

  const certifications = profile.certifications || [];

  // Add new certification
  const handleAddCertification = () => {
    if (!newCert.name.trim()) {return;}

    const cert = {
      id: Date.now().toString(),
      ...newCert,
    };

    onChange('certifications', [...certifications, cert]);
    setNewCert({ name: '', issuer: '', issue_date: '', expiry_date: '' });
    setShowAddCert(false);
  };

  // Remove certification
  const handleRemoveCertification = (certId) => {
    onChange('certifications', certifications.filter(c => c.id !== certId));
  };

  // Check if certification is expiring soon (within 60 days)
  const isCertExpiringSoon = (expiryDate) => {
    if (!expiryDate) {return false;}
    const expiry = new Date(expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.floor((expiry - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 60 && daysUntilExpiry > 0;
  };

  const isCertExpired = (expiryDate) => {
    if (!expiryDate) {return false;}
    const expiry = new Date(expiryDate);
    const now = new Date();
    return expiry < now;
  };

  // Check if insurance is expiring soon (within 60 days)
  const isExpiringSoon = () => {
    if (!profile.insurance_expiry) {return false;}
    const expiry = new Date(profile.insurance_expiry);
    const now = new Date();
    const daysUntilExpiry = Math.floor((expiry - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 60 && daysUntilExpiry > 0;
  };

  const isExpired = () => {
    if (!profile.insurance_expiry) {return false;}
    const expiry = new Date(profile.insurance_expiry);
    const now = new Date();
    return expiry < now;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Credentials & Insurance</h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage your professional licenses and insurance information
          </p>
        </div>

        {/* Guide License */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Guide License</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* License Number */}
            <div>
              <label htmlFor="license_number" className="block text-sm font-medium text-gray-700 mb-1">
                License Number
              </label>
              <input
                type="text"
                id="license_number"
                value={profile.license_number || ''}
                onChange={(e) => onChange('license_number', e.target.value)}
                placeholder="e.g., GL-12345"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* License State */}
            <div>
              <label htmlFor="license_state" className="block text-sm font-medium text-gray-700 mb-1">
                Issuing State
              </label>
              <select
                id="license_state"
                value={profile.license_state || ''}
                onChange={(e) => onChange('license_state', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select state</option>
                {US_STATES.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Insurance */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Liability Insurance</h3>

          {/* Expiry Warning */}
          {isExpired() && (
            <div className="mb-4 flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-md">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Insurance Expired</p>
                <p className="text-xs text-red-700 mt-1">
                  Your insurance expired on {new Date(profile.insurance_expiry).toLocaleDateString()}.
                  Please update your policy information.
                </p>
              </div>
            </div>
          )}

          {isExpiringSoon() && !isExpired() && (
            <div className="mb-4 flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Insurance Expiring Soon</p>
                <p className="text-xs text-amber-700 mt-1">
                  Your insurance expires on {new Date(profile.insurance_expiry).toLocaleDateString()}.
                  Consider renewing soon.
                </p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Insurance Provider */}
            <div>
              <label htmlFor="insurance_provider" className="block text-sm font-medium text-gray-700 mb-1">
                Insurance Provider
              </label>
              <input
                type="text"
                id="insurance_provider"
                value={profile.insurance_provider || ''}
                onChange={(e) => onChange('insurance_provider', e.target.value)}
                placeholder="e.g., Outdoor Guides Insurance Co."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Policy Number */}
            <div>
              <label htmlFor="insurance_policy_number" className="block text-sm font-medium text-gray-700 mb-1">
                Policy Number
              </label>
              <input
                type="text"
                id="insurance_policy_number"
                value={profile.insurance_policy_number || ''}
                onChange={(e) => onChange('insurance_policy_number', e.target.value)}
                placeholder="e.g., POL-2025-123456"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Expiry Date */}
            <div>
              <label htmlFor="insurance_expiry" className="block text-sm font-medium text-gray-700 mb-1">
                Expiry Date
              </label>
              <input
                type="date"
                id="insurance_expiry"
                value={profile.insurance_expiry || ''}
                onChange={(e) => onChange('insurance_expiry', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                  isExpired() ? 'border-red-300 bg-red-50' : isExpiringSoon() ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
                }`}
              />
            </div>
          </div>
        </div>

        {/* Certifications Management (MVP 1.21.3 - US-1.21.3-14) */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Certifications</h3>
            {!showAddCert && (
              <button
                onClick={() => setShowAddCert(true)}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Add Certification</span>
              </button>
            )}
          </div>

          {/* Existing Certifications */}
          {certifications.length > 0 && (
            <div className="space-y-3 mb-4">
              {certifications.map((cert) => {
                const isExpired = isCertExpired(cert.expiry_date);
                const isExpiring = isCertExpiringSoon(cert.expiry_date);

                return (
                  <div
                    key={cert.id}
                    className={`p-4 rounded-lg border ${
                      isExpired
                        ? 'bg-red-50 border-red-200'
                        : isExpiring
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h4 className="font-semibold text-gray-900">{cert.name}</h4>
                          {isExpired && (
                            <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-medium rounded">
                              Expired
                            </span>
                          )}
                          {isExpiring && !isExpired && (
                            <span className="px-2 py-0.5 bg-amber-600 text-white text-xs font-medium rounded">
                              Expiring Soon
                            </span>
                          )}
                        </div>
                        {cert.issuer && (
                          <p className="text-sm text-gray-600 mt-1">Issued by: {cert.issuer}</p>
                        )}
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                          {cert.issue_date && (
                            <span>
                              Issued: {new Date(cert.issue_date).toLocaleDateString()}
                            </span>
                          )}
                          {cert.expiry_date && (
                            <span className={isExpired ? 'text-red-600 font-semibold' : isExpiring ? 'text-amber-600 font-semibold' : ''}>
                              Expires: {new Date(cert.expiry_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveCertification(cert.id)}
                        className="flex-shrink-0 text-gray-400 hover:text-red-600 transition-colors"
                        aria-label="Remove certification"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add Certification Form */}
          {showAddCert && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900">Add New Certification</h4>
                <button
                  onClick={() => {
                    setShowAddCert(false);
                    setNewCert({ name: '', issuer: '', issue_date: '', expiry_date: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Certification Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={newCert.name}
                  onChange={(e) => setNewCert({ ...newCert, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select certification</option>
                  {CERTIFICATION_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Issuing Organization
                </label>
                <input
                  type="text"
                  value={newCert.issuer}
                  onChange={(e) => setNewCert({ ...newCert, issuer: e.target.value })}
                  placeholder="e.g., American Red Cross"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Issue Date
                  </label>
                  <input
                    type="date"
                    value={newCert.issue_date}
                    onChange={(e) => setNewCert({ ...newCert, issue_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expiry Date
                  </label>
                  <input
                    type="date"
                    value={newCert.expiry_date}
                    onChange={(e) => setNewCert({ ...newCert, expiry_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <button
                onClick={handleAddCertification}
                disabled={!newCert.name.trim()}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                <span>Add Certification</span>
              </button>
            </div>
          )}

          {certifications.length === 0 && !showAddCert && (
            <p className="text-sm text-gray-500 text-center py-4">
              No certifications added yet. Click &quot;Add Certification&quot; to get started.
            </p>
          )}
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onSave}
            disabled={isSaving}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

CredentialsTab.propTypes = {
  profile: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};
